import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { createItem, updateItem, deleteItem } from 'services/shipments.service'
import { toNumberOrNull } from 'utils/format'
import { useI18n, apiError } from 'i18n'

// Labels via i18n (`items.columns.<key>`).
const COLUMNS = [
  { key: 'sku' },
  { key: 'description', wide: true },
  { key: 'quantity', type: 'number' },
  { key: 'amount', type: 'number' },
  { key: 'currency' },
  { key: 'tariffFraction' },
  { key: 'igiRate', type: 'pct' },
  { key: 'grossWeightKg', type: 'number', basis: true },
  { key: 'volumeCbm', type: 'number', basis: true }
]

const toForm = (item) => Object.fromEntries(COLUMNS.map((c) => {
  const v = item[c.key]
  if (v === null || v === undefined) return [c.key, '']
  return [c.key, c.type === 'pct' ? String(Math.round(v * 100000) / 1000) : String(v)]
}))

const toBody = (form) => Object.fromEntries(COLUMNS.map((c) => {
  if (c.type === 'number') return [c.key, toNumberOrNull(form[c.key])]
  if (c.type === 'pct') {
    const n = toNumberOrNull(form[c.key])
    return [c.key, n === null ? null : n / 100]
  }
  return [c.key, form[c.key] === '' ? null : form[c.key]]
}))

const cellInput = 'w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-brand focus:outline-none'

const ItemsTab = ({ shipment, readOnly, onChange }) => {
  const { t, fmt } = useI18n()
  const [editing, setEditing] = useState(null) // itemId | 'new'
  const [form, setForm] = useState({})

  const start = (item) => { setEditing(item ? item.itemId : 'new'); setForm(toForm(item || { currency: 'USD' })) }
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const save = async () => {
    try {
      if (editing === 'new') await createItem(shipment.shipmentId, toBody(form))
      else await updateItem(shipment.shipmentId, editing, toBody(form))
      setEditing(null)
      onChange()
    } catch (error) {
      toast.error(apiError(error, t('items.saveFailed')))
    }
  }

  const remove = async (item) => {
    if (!window.confirm(t('items.confirmDelete', { name: item.sku || item.description }))) return
    try {
      await deleteItem(shipment.shipmentId, item.itemId)
      onChange()
    } catch (error) {
      toast.error(apiError(error, t('items.deleteFailed')))
    }
  }

  // Weights/volumes only matter when some line is spread by them; flag gaps anyway.
  const missing = (item, col) => col.basis && (item[col.key] === null || item[col.key] === undefined)
  const missingBasis = shipment.items.some((it) => COLUMNS.some((c) => missing(it, c)))
  const needsIgi = shipment.mode === 'estimate' && shipment.items.some((it) => it.igiRate === null)

  const renderEditRow = (key) => (
    <tr key={key} className='bg-sky-50/50'>
      {COLUMNS.map((c) => (
        <td key={c.key} className='px-2 py-2'>
          <input type={c.type ? 'number' : 'text'} step='any' value={form[c.key]} onChange={set(c.key)} className={cellInput} />
        </td>
      ))}
      <td className='px-2 py-2'>
        <div className='flex justify-end gap-3'>
          <button onClick={save} className='font-semibold text-brand hover:underline'>{t('common.save')}</button>
          <button onClick={() => setEditing(null)} className='text-slate-500 hover:underline'>{t('common.cancel')}</button>
        </div>
      </td>
    </tr>
  )

  return (
    <div className='space-y-3'>
      <p className='text-sm text-slate-500'>{t('items.help')}</p>
      {missingBasis && (
        <p className='text-sm text-status-review'>{t('items.missingBasis')}</p>
      )}
      {needsIgi && <p className='text-sm text-status-review'>{t('items.needsIgi')}</p>}

      <div className='overflow-x-auto rounded-xl border border-slate-200 bg-white'>
        <table className='min-w-full divide-y divide-slate-200 text-sm'>
          <thead className='bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500'>
            <tr>
              {COLUMNS.map((c) => <th key={c.key} className={`px-3 py-3 ${c.type ? 'text-right' : ''} ${c.wide ? 'min-w-[220px]' : ''}`}>{t(`items.columns.${c.key}`)}</th>)}
              <th className='px-3 py-3 text-right'>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {shipment.items.length === 0 && editing !== 'new' && (
              <tr><td colSpan={COLUMNS.length + 1} className='px-4 py-8 text-center text-slate-400'>{t('items.empty')}</td></tr>
            )}
            {shipment.items.map((it) => (editing === it.itemId
              ? renderEditRow(it.itemId)
              : (
                <tr key={it.itemId} className='hover:bg-slate-50'>
                  {COLUMNS.map((c) => (
                    <td key={c.key} className={`px-3 py-2 ${c.type ? 'text-right tabular-nums' : ''} ${missing(it, c) ? 'bg-orange-50 text-status-review' : 'text-slate-700'}`}>
                      {c.key === 'amount'
                        ? fmt.money(it.amount)
                        : c.type === 'pct'
                          ? (it.igiRate === null ? '—' : `${fmt.number(it.igiRate * 100, 3)}%`)
                          : c.type === 'number' ? fmt.number(it[c.key]) : (it[c.key] ?? '—')}
                    </td>
                  ))}
                  <td className='px-3 py-2'>
                    {!readOnly && (
                      <div className='flex justify-end gap-3'>
                        <button onClick={() => start(it)} className='font-semibold text-brand hover:underline'>{t('common.edit')}</button>
                        <button onClick={() => remove(it)} className='font-semibold text-status-incomplete hover:underline'>{t('common.delete')}</button>
                      </div>
                    )}
                  </td>
                </tr>
                )
            ))}
            {editing === 'new' && renderEditRow('new')}
          </tbody>
        </table>
      </div>
      {!readOnly && editing === null && (
        <button onClick={() => start(null)} className='text-sm font-semibold text-brand hover:underline'>{t('items.add')}</button>
      )}
    </div>
  )
}

export default ItemsTab
